import type {
  StockMachineAttachmentDataDto,
  StockMachineAttachmentDto,
  StockMachineDataDto,
  StockMachineDto
} from '@api/machines'
import { formatDateAndTime, getEnvDateFormat, StockTypes } from '@common/utils'
import {
  type ColumnCellTemplateData,
  MolsonBox,
  MolsonTypography
} from '@components/reusable'
import { useTranslation } from 'react-i18next'
import { useGetStockItemsByIdAndStock } from '@/shared-queries/machines/index'
import { CustomMolsonAccordion, StyledGrid } from '../styled-components/styles'

const renderStatusColumn = (params: ColumnCellTemplateData) => {
  return (
    <MolsonBox
      display="flex"
      alignItems="center"
      gap={2}
      justifyContent="space-between"
    >
      <MolsonTypography
        title={params.data.status}
        noWrap={true}
        variant="body2"
      >
        {params.data.status}
      </MolsonTypography>
      {params.data?.employeeName && (
        <MolsonTypography title={params.data.employeeName} whiteSpace="wrap">
          {params?.data?.employeeName}
        </MolsonTypography>
      )}
    </MolsonBox>
  )
}

const renderConfigurationColumn = (params: ColumnCellTemplateData) => {
  const maxTextLength = 25

  const truncateText = (text: string) => {
    if (text.length > maxTextLength) {
      return text.slice(0, maxTextLength)
    }
    return text
  }

  const remainingText =
    params.data.configuration.length > maxTextLength
      ? params.data.configuration.slice(maxTextLength)
      : ''

  return (
    <CustomMolsonAccordion
      sx={{
        '.MuiTypography-root': {
          fontWeight: 'normal'
        },
        background: 'transparent'
      }}
      primaryText={truncateText(params.data.configuration)}
      primaryTextVariant="body2"
      summarySx={{ borderBottom: 'none', padding: 0 }}
    >
      <MolsonBox
        sx={{
          whiteSpace: 'pre-wrap'
        }}
        display="flex"
        flexWrap="wrap"
      >
        {remainingText}
      </MolsonBox>
    </CustomMolsonAccordion>
  )
}

type StockTypeProps = {
  equipment: StockMachineAttachmentDto | StockMachineDto | null
  onRowClick: (
    equipment: StockMachineAttachmentDataDto | StockMachineDataDto
  ) => void
  machineType?: 'attachment' | 'machine'
}

export const SelectStockType = ({
  equipment,
  onRowClick,
  machineType
}: StockTypeProps) => {
  const { t } = useTranslation('', {
    keyPrefix: 'common',
    useSuspense: false
  })

  const { data: stockItems = [] } = useGetStockItemsByIdAndStock({
    id: equipment?.id ?? 0,
    stockType: equipment?.stockType as StockTypes,
    machineType: machineType
  })

  const columnType =
    equipment?.stockType === StockTypes.IN_STOCK
      ? {
          alignment: 'right',
          caption: t('daysInStock'),
          dataField: 'daysInStock',
          dataType: 'number',
          width: 150
        }
      : {
          alignment: 'right',
          caption: t('expectedArrivalDate'),
          dataField: 'expectedArrivalDate',
          dataType: 'date',
          format: getEnvDateFormat(true),
          cellRender: ({ value }: { value: string }) => {
            return formatDateAndTime(value, {
              date: true,
              time: false
            })
          },
          width: 150
        }

  return (
    <StyledGrid
      key={`stock-grid-${equipment?.id}`}
      columnsProps={[
        {
          caption: t('unitNumber') ?? '',
          dataField: 'unitNumber',
          width: 170
        },
        {
          caption: t('unitDescription') ?? '',
          dataField: 'unitDescription',
          customizeText: params => params.value ?? 'N/A',
          width: 170
        },
        {
          alignment: 'left',
          caption: t('status') ?? '',
          cellRender: renderStatusColumn,
          dataField: 'status',
          width: 150
        },
        {
          alignment: 'left',
          caption: t('location') ?? '',
          dataField: 'location',
          width: 250
        },
        // @ts-ignore
        columnType,
        {
          alignment: 'left',
          caption: t('configuration') ?? '',
          cellRender: renderConfigurationColumn,
          dataField: 'configuration'
        }
      ]}
      headerFilterProps={{
        visible: true
      }}
      onCellClick={e => {
        if (e.columnIndex === 4) {
          e.event?.stopImmediatePropagation()
        }
      }}
      onCellPrepared={e => {
        if (e.rowType === 'data' && e.columnIndex !== 4) {
          e.cellElement.style.setProperty('cursor', 'pointer', 'important')
        }
      }}
      onSelectionChanged={selectedRows => {
        const { selectedRowsData } = selectedRows

        const selectedRow = selectedRowsData[0]
        onRowClick(selectedRow)
      }}
      searchPanelProps={{
        highlightSearchText: true,
        visible: true
      }}
      selection={{
        mode: 'single'
      }}
      sx={{
        '.dx-datagrid .dx-datagrid-content .dx-datagrid-table .dx-row>td:not(.dx-validation-pending):not(.dx-datagrid-select-all)':
          {
            verticalAlign: 'baseline'
          }
      }}
      dataSource={stockItems}
    />
  )
}
